import { useFormikContext } from 'formik';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NIGHT_EYE_IMG_URL } from '../../../Process';

const AdharUpload = ({ isMultipleShopAvailble }) => {
  const { t } = useTranslation();
  const { values, touched, errors, setFieldValue } = useFormikContext();

  const [showModal, setShowModal] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const hasFrontImage = !!values.aadhaarFront || !!values.oldAdharFronImage;

  // Disable upload if old front image exists and multiple shops mode is active
  const isDisabled = isMultipleShopAvailble && !!values?.oldAdharFronImage;

  // ── Camera Functions ───────────────────────────────────────────────
  const openCamera = async () => {
    if (isDisabled) return;
    setCameraError(null);
    setCameraActive(true);

    try {
      let stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      // Fallback for desktop (most have only user-facing camera)
      if (stream && stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        if (!settings.width || settings.width === 0) {
          stream.getTracks().forEach(t => t.stop());
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(
        err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
          ? 'Camera permission denied. Please allow access in browser settings.'
          : 'Could not open camera. Try choosing from gallery instead.'
      );
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `aadhaar-front-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setFieldValue('aadhaarFront', file);
        closeCamera();
        setShowModal(false);
      },
      'image/jpeg',
      0.85
    );
  };

  const closeCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraError(null);
  };

  useEffect(() => {
    return () => closeCamera();
  }, []);

  // ── File Handling ──────────────────────────────────────────────────
  const handleGalleryClick = () => {
    if (isDisabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFieldValue('aadhaarFront', file);
      closeCamera();
      setShowModal(false);
    }
    e.target.value = ''; // reset for re-upload same file if needed
  };

  const openModal = () => {
    if (isDisabled) return;
    setShowModal(true);
    closeCamera();
  };

  return (
    <>
      <span className="text-xs font-bold font-medium text-slate-700 dark:text-slate-300">
        {t('frontSideAadhaar')} <span className="text-red-800">*</span>
      </span>

      <div className="relative">
        <div
          onClick={openModal}
          className={`group relative flex flex-col items-center justify-center w-full h-56 border-2 cursor-pointer ${
            isDisabled
              ? 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/30 cursor-not-allowed'
              : touched.aadhaarFront && errors.aadhaarFront
              ? 'border-red-500'
              : 'border-dashed border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
          } rounded-xl bg-slate-50 dark:bg-slate-800/50 transition-colors min-h-[220px]`}
        >
          {hasFrontImage ? (
            <div className="w-full h-full p-4 flex items-center justify-center relative">
              {values.aadhaarFront?.type?.startsWith('image/') || values.oldAdharFronImage ? (
                <img
                  src={
                    values.aadhaarFront
                      ? URL.createObjectURL(values.aadhaarFront)
                      : `${NIGHT_EYE_IMG_URL}/SmartCollection/AadhaarCardImage/${values.oldAdharFronImage}`
                  }
                  alt="Aadhaar Front"
                  className="max-w-full max-h-full object-contain rounded"
                />
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <span className="material-symbols-outlined text-5xl text-primary">description</span>
                  <p className="text-xs font-bold text-slate-600 dark:text-slate-400">
                    {values.aadhaarFront?.name || 'Uploaded PDF'}
                  </p>
                </div>
              )}

              {/* Remove button - only if not disabled/old image */}
              {!isDisabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFieldValue('aadhaarFront', null);
                  }}
                  className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="p-3 bg-primary/10 rounded-full text-primary">
                <span className="material-symbols-outlined text-3xl">cloud_upload</span>
              </div>
              <p className="text-xs font-bold font-medium text-slate-700 dark:text-slate-200 mt-2">
                <span className="text-primary hover:underline">{t('clickToUpload')}</span> front side
              </p>
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                JPG, PNG or PDF (max 5MB)
              </p>
            </>
          )}

          {/* Secure badge */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded text-green-700 dark:text-green-400 text-xs font-bold font-medium">
            <span className="material-symbols-outlined text-[14px]">verified_user</span>
            {t('secureUpload')}
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileChange}
          className="hidden"
          disabled={isDisabled}
        />
      </div>

      {touched.aadhaarFront && errors.aadhaarFront && (
        <p className="text-red-800 text-xs font-bold mt-1">{errors.aadhaarFront}</p>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Upload Aadhaar Front Side
              </h3>
              <button
              type="button"
                onClick={() => {
                  setShowModal(false);
                  closeCamera();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {cameraActive ? (
                <div className="space-y-5">
                  <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3] border border-gray-700 shadow-inner">
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      autoPlay
                      playsInline
                      muted
                    />
                    <canvas ref={canvasRef} className="hidden" />
                  </div>

                  {cameraError && (
                    <p className="text-red-600 text-sm text-center bg-red-50 dark:bg-red-950/30 p-3 rounded">
                      {cameraError}
                    </p>
                  )}

                  <div className="flex gap-4">
                    <button
                    type="button"
                      onClick={closeCamera}
                      className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                    type="button"
                      onClick={capturePhoto}
                      disabled={!!cameraError}
                      className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Capture Photo
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-5">
                  <button
                  type="button"
                    onClick={openCamera}
                    className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-6xl text-blue-600">photo_camera</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">Take Photo</span>
                  </button>

                  <button
                  type="button"
                    onClick={handleGalleryClick}
                    className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="material-symbols-outlined text-6xl text-green-600">photo_library</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">Gallery</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdharUpload;